import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { AuthContext } from "./auth-context";
import type { AuthContextValue } from "./auth-context";
import {
  beginLogin,
  buildLogoutUrl,
  completeLogin,
  consumeReturnTo,
  fetchUserInfo,
  isCallbackUrl,
  mergeClaims,
  readClaims,
  readOidcConfig,
  refreshTokens,
} from "../utils/oidc";
import type { TokenSet, UserClaims } from "../utils/oidc";

/** Renew when this fraction of the token's lifetime has elapsed. */
const RENEW_AT_LIFETIME_FRACTION = 0.75;

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Drives the KochID Authorization Code + PKCE flow and holds the resulting
 * tokens (ADR 0012, conventions/frontend-stack.md).
 *
 * Tokens live in React state and a ref — in memory only. Nothing is written to
 * localStorage or sessionStorage except the single-use PKCE verifier and state,
 * which must survive the redirect and are deleted at exchange time (see
 * utils/oidc.ts). A page reload therefore drops the session and re-runs the
 * flow, which is the intended trade: no token is recoverable from browser
 * storage by injected script.
 */
function AuthProvider({ children }: AuthProviderProps) {
  const config = useMemo(() => readOidcConfig(), []);
  const [tokens, setTokens] = useState<TokenSet | null>(null);
  const [user, setUser] = useState<UserClaims | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Mirrors `tokens` so the renewal timer can read the current value without
  // being re-created on every render.
  const tokensRef = useRef<TokenSet | null>(null);
  const renewalTimer = useRef<number | null>(null);

  const applyTokens = useCallback(
    (next: TokenSet) => {
      tokensRef.current = next;
      setTokens(next);
      setError(null);

      // Show whatever the ID token carries immediately, so the shell never
      // waits on a second network call to render.
      const fromIdToken = readClaims(next.idToken);
      setUser(fromIdToken);

      // KochID's ID token is minimal — `sub` is reliably present, `name` and
      // `email` frequently are not. Fill the gaps from UserInfo, which is
      // where this provider actually releases profile claims. Failure is
      // non-fatal: the session stays valid and the display degrades.
      if (config) {
        fetchUserInfo(config, next.accessToken).then((extra) => {
          if (extra) {
            setUser((current) => mergeClaims(current ?? fromIdToken, extra));
          }
        });
      }
    },
    [config],
  );

  const clearSession = useCallback(() => {
    tokensRef.current = null;
    setTokens(null);
    setUser(null);
    if (renewalTimer.current !== null) {
      window.clearTimeout(renewalTimer.current);
      renewalTimer.current = null;
    }
  }, []);

  const login = useCallback(() => {
    if (!config) {
      setError("KochID is not configured for this environment.");
      return;
    }
    const returnTo = `${window.location.pathname}${window.location.search}`;
    beginLogin(config, returnTo).catch((cause: unknown) => {
      setError(cause instanceof Error ? cause.message : "Could not start login.");
    });
  }, [config]);

  const logout = useCallback(() => {
    const current = tokensRef.current;
    clearSession();

    if (!config || !current) {
      window.location.assign("/");
      return;
    }

    buildLogoutUrl(config, current.idToken)
      .then((url) => {
        window.location.assign(url ?? "/");
      })
      .catch(() => {
        // An unreachable discovery document must not trap the user in a
        // signed-in-looking UI — the local session is already cleared.
        window.location.assign("/");
      });
  }, [clearSession, config]);

  /**
   * Schedule a silent renewal. With a refresh token the renewal is invisible.
   * Without one — KochID issues refresh tokens only when `offline_access` is
   * requested — the session simply expires and the user is sent back through
   * the flow, which is typically seamless while the IdP session is alive.
   */
  const scheduleRenewal = useCallback(
    (current: TokenSet) => {
      if (renewalTimer.current !== null) {
        window.clearTimeout(renewalTimer.current);
      }
      if (!config) {
        return;
      }

      const lifetime = current.expiresAt - Date.now();
      const delay = Math.max(lifetime * RENEW_AT_LIFETIME_FRACTION, 0);

      renewalTimer.current = window.setTimeout(() => {
        const latest = tokensRef.current;
        if (!latest) {
          return;
        }
        if (!latest.refreshToken) {
          clearSession();
          return;
        }
        refreshTokens(config, latest.refreshToken)
          .then(applyTokens)
          .catch(() => {
            // Refresh failed — drop the session rather than keep a stale
            // token. The UI will present sign-in again.
            clearSession();
          });
      }, delay);
    },
    [applyTokens, clearSession, config],
  );

  // Complete the redirect callback on first mount, if this is one.
  useEffect(() => {
    let cancelled = false;

    if (!config) {
      setIsLoading(false);
      return;
    }

    if (!isCallbackUrl()) {
      setIsLoading(false);
      return;
    }

    completeLogin(config)
      .then((next) => {
        if (cancelled) {
          return;
        }
        applyTokens(next);
        // Strip code/state from the URL so a reload cannot replay the
        // callback, and restore where the user was before login.
        window.history.replaceState({}, document.title, consumeReturnTo());
      })
      .catch((cause: unknown) => {
        if (cancelled) {
          return;
        }
        setError(cause instanceof Error ? cause.message : "Login failed.");
        window.history.replaceState({}, document.title, "/");
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [applyTokens, config]);

  // (Re)schedule renewal whenever the token set changes.
  useEffect(() => {
    if (tokens) {
      scheduleRenewal(tokens);
    }
    return () => {
      if (renewalTimer.current !== null) {
        window.clearTimeout(renewalTimer.current);
        renewalTimer.current = null;
      }
    };
  }, [scheduleRenewal, tokens]);

  const value: AuthContextValue = useMemo(
    () => ({
      user,
      isAuthenticated: tokens !== null,
      isLoading,
      accessToken: tokens?.accessToken ?? null,
      error,
      isConfigured: config !== null,
      login,
      logout,
    }),
    [config, error, isLoading, login, logout, tokens, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthProvider;
